import { useEffect, useRef, useState } from "react";
import OceanDive from "@/game/oceandive";
import { preloadAssets } from "@/game/assets";
import { AudioManager } from "@/game/audio";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { Volume2, VolumeX, Heart, Settings } from "lucide-react";
import BonusPauseMenu from "@/components/BonusPauseMenu";
import { BonusLeaderboard } from "@/components/BonusLeaderboard";
import { stopTitleAudio } from "@/lib/audioBus";

const BONUS_PER_COIN = 75;
const base = `${process.env.PUBLIC_URL || ""}/game-assets`;
const STICK_R = 42; // px of knob travel from center

// Single-axis analog thumbstick. axis="x" → left/right, axis="y" → up/down.
function AxisStick({ axis, onChange, testid, glyph }) {
  const baseRef = useRef(null);
  const activeRef = useRef(false);
  const [val, setVal] = useState(0);

  const compute = (e) => {
    const r = baseRef.current.getBoundingClientRect();
    const c = axis === "x" ? r.left + r.width / 2 : r.top + r.height / 2;
    const p = axis === "x" ? e.clientX : e.clientY;
    const v = Math.max(-1, Math.min(1, (p - c) / STICK_R));
    setVal(v); onChange(v);
  };
  const start = (e) => { e.preventDefault(); activeRef.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); compute(e); };
  const move = (e) => { if (activeRef.current) { e.preventDefault(); compute(e); } };
  const release = () => { if (!activeRef.current) return; activeRef.current = false; setVal(0); onChange(null); };

  const knobStyle = axis === "x"
    ? { transform: `translate(${val * STICK_R}px, 0)` }
    : { transform: `translate(0, ${val * STICK_R}px)` };

  return (
    <div
      ref={baseRef}
      data-testid={testid}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={release}
      onPointerCancel={release}
      onPointerLeave={release}
      className="relative rounded-full bg-black/35 border-2 border-white/25 backdrop-blur-sm flex items-center justify-center select-none"
      style={{ width: 132, height: 132, touchAction: "none" }}
    >
      {/* axis guide */}
      <span className="absolute text-white/40 font-pixel text-[9px] tracking-widest">{glyph}</span>
      {/* knob */}
      <div className="rounded-full bg-[#0fb6d6]/90 border-2 border-white/70 shadow-lg pointer-events-none"
        style={{ width: 56, height: 56, ...knobStyle, transition: activeRef.current ? "none" : "transform 0.12s ease-out" }} />
    </div>
  );
}

export default function OceanDiveRound({ character, onContinue, onExit }) {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  const triesRef = useRef(1); // one extra try to continue from where you left off

  const [phase, setPhase] = useState("loading"); // loading | countdown | running | knockedOut | final
  const [countNum, setCountNum] = useState(3);
  const [progress, setProgress] = useState({ collected: 0, total: 80, pct: 0, hits: 0, maxHits: 3 });
  const [result, setResult] = useState(null);
  const [koData, setKoData] = useState(null);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);

  const openPause = () => { const g = gameRef.current; if (g) g.paused = true; setPaused(true); };
  const resumePlay = () => { gameRef.current?.begin(); setPaused(false); };

  useEffect(() => { stopTitleAudio(); }, []);

  useEffect(() => {
    let game;
    let cancelled = false;
    preloadAssets(["ocean_chest", "ocean_trophy", "fish_a", "fish_b", "fish_c", character?.id]).then((assets) => {
      if (cancelled) return;
      const audio = new AudioManager();
      audioRef.current = audio;
      game = new OceanDive(canvasRef.current, {
        character,
        assets,
        audio,
        callbacks: {
          onProgress: setProgress,
          onComplete: ({ collected, total, completed }) => {
            if (completed) {
              setResult({ collected, total, bonus: collected * BONUS_PER_COIN, saved: !!user, completed: true });
              setPhase("final");
              if (user) api.completeDive(collected).then(() => refreshUser()).catch(() => {});
            } else if (triesRef.current > 0) {
              triesRef.current -= 1;
              setKoData({ collected });
              setPhase("knockedOut");
            } else {
              setResult({ collected, total, bonus: collected * BONUS_PER_COIN, saved: false, completed: false });
              setPhase("final");
            }
          },
        },
      });
      gameRef.current = game;
      game.start();
      setPhase("countdown");
    });
    return () => {
      cancelled = true;
      game?.destroy();
      audioRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "countdown") return;
    setCountNum(3);
    const tm = [];
    tm.push(setTimeout(() => setCountNum(2), 750));
    tm.push(setTimeout(() => setCountNum(1), 1500));
    tm.push(setTimeout(() => setCountNum("GO"), 2250));
    tm.push(setTimeout(() => { gameRef.current?.begin(); setPhase("running"); }, 3000));
    return () => tm.forEach(clearTimeout);
  }, [phase]);

  const toggleMute = () => { const m = !muted; setMuted(m); audioRef.current?.setMuted(m); };
  const resume = () => { gameRef.current?.continueDive(); setPhase("countdown"); };
  const setAxis = (which, v) => gameRef.current?.setAxis(which, v);

  return (
    <div className="relative w-full h-full bg-[#06243a] overflow-hidden" style={{ touchAction: "none" }} data-testid="ocean-dive-round">
      <canvas id="ocean-canvas" ref={canvasRef} width={960} height={540} />

      {/* Top HUD */}
      <div className="absolute top-0 left-0 w-full p-3 md:p-4 flex justify-between items-start z-40 pointer-events-none font-pixel pixel-stroke text-white">
        <div className="flex flex-col gap-1">
          <div className="text-[10px] md:text-sm text-[#7fe9ff]">{t("oceanTitle")}</div>
          <div data-testid="ocean-count" className="text-base md:text-2xl text-white">
            C$ {progress.collected} <span className="text-white/60 text-xs md:text-base">/ {progress.total}</span>
          </div>
          <div className="flex gap-1 mt-0.5" data-testid="ocean-lives">
            {Array.from({ length: progress.maxHits || 3 }).map((_, i) => (
              <Heart key={i} size={16} strokeWidth={2.5}
                className={i < (progress.maxHits - progress.hits) ? "text-[#ff5470]" : "text-white/25"}
                fill={i < (progress.maxHits - progress.hits) ? "#ff5470" : "transparent"} />
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="text-[8px] md:text-[10px] text-white/80">{t("oceanSteer")}</div>
          <div className="mt-1 w-40 md:w-56 h-3 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full rounded-full bg-[#0fb6d6] transition-all duration-150" style={{ width: `${progress.pct}%` }} />
          </div>
          <div className="text-[7px] md:text-[9px] text-white/60">{t("oceanDepth")}</div>
        </div>
        <div className="pointer-events-auto flex gap-2">
          {phase === "running" && (
            <button data-testid="ocean-settings-btn" onClick={openPause} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Settings size={16} strokeWidth={2.5} />
            </button>
          )}
          <button data-testid="ocean-mute-btn" onClick={toggleMute} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
            {muted ? <VolumeX size={16} strokeWidth={2.5} /> : <Volume2 size={16} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <BonusPauseMenu open={paused} onResume={resumePlay} onExit={() => onExit && onExit()} muted={muted} onToggleSound={toggleMute} />

      {/* Analog swim sticks — LEFT stick = left/right, RIGHT stick = up/down */}
      {phase === "running" && (
        <>
          <div className="touch-only absolute bottom-7 left-6 z-40 flex flex-col items-center gap-1">
            <AxisStick axis="x" testid="ocean-stick-x" glyph="◄ ►" onChange={(v) => setAxis("x", v)} />
          </div>
          <div className="touch-only absolute bottom-7 right-6 z-40 flex flex-col items-center gap-1">
            <AxisStick axis="y" testid="ocean-stick-y" glyph="▲ ▼" onChange={(v) => setAxis("y", v)} />
          </div>
        </>
      )}

      {/* Loading */}
      {phase === "loading" && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#06243a]">
          <p className="font-pixel text-[#7fe9ff] text-sm blink">{t("loadingGame")}</p>
        </div>
      )}

      {/* Countdown */}
      {phase === "countdown" && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/45" data-testid="ocean-countdown">
          <div className="font-pixel text-[10px] md:text-sm text-[#7fe9ff] pixel-stroke mb-4">{t("oceanTitle")}</div>
          <div key={String(countNum)} className="font-pixel pixel-stroke pop-in text-7xl md:text-9xl"
            style={{ color: countNum === "GO" ? "#4CAF50" : "#0fb6d6" }} data-testid="ocean-countdown-num">
            {countNum === "GO" ? t("bonusCountGo") : countNum}
          </div>
        </div>
      )}

      {/* Knocked out — one more try, resume from here */}
      {phase === "knockedOut" && koData && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 scanlines">
          <div className="pixel-card bg-[#3a1320] p-6 md:p-8 w-full max-w-sm text-center pop-in" data-testid="ocean-ko-modal">
            <div className="text-4xl mb-1">🐟💥</div>
            <h2 className="font-pixel text-lg md:text-2xl pixel-stroke text-[#ff7a7a]">{t("diveKO")}</h2>
            <p className="font-vt text-white/85 text-sm md:text-base mt-2 leading-snug">{t("diveKOBody")}</p>
            <p className="font-pixel text-[10px] text-[#7fe9ff] mt-3">C$ {koData.collected}</p>
            <button data-testid="ocean-continue-here-btn" onClick={resume} className="pixel-btn bg-[#0fb6d6] text-white px-6 py-3 text-xs w-full mt-4 blink">
              {t("diveContinueHere")}
            </button>
          </div>
        </div>
      )}

      {/* Trophy result (reached the chest) */}
      {phase === "final" && result && result.completed && (
        <div className="absolute inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/85 p-3 overflow-y-auto scanlines">
          <div className="pixel-card bg-[#0b2b44] p-4 sm:p-6 md:p-8 w-full max-w-md text-center pop-in my-auto max-h-[96vh] overflow-y-auto" data-testid="ocean-result-modal">
            <h2 className="font-pixel text-lg sm:text-2xl md:text-3xl pixel-stroke text-[#FFD24A]">{t("oceanResultTitle")}</h2>
            <img src={`${base}/ocean_trophy.webp`} alt="trophy" className="w-16 h-16 sm:w-28 sm:h-28 md:w-36 md:h-36 mx-auto my-1 sm:my-2 drop-shadow-[0_0_18px_rgba(255,210,74,0.6)] trophy-bounce" data-testid="ocean-trophy-img" />
            <p className="font-pixel text-[9px] sm:text-[10px] md:text-xs text-[#FFD24A]">{t("trophyTitle")}</p>
            <div className="my-2 sm:my-4 py-2 sm:py-3 border-y-2 border-white/15">
              <p className="font-pixel text-[9px] sm:text-[10px] text-[#7fe9ff]">{t("cordobas")}</p>
              <p className="font-pixel text-3xl sm:text-4xl md:text-5xl text-white mt-1 sm:mt-2" data-testid="ocean-final-count">{result.collected}<span className="text-white/50 text-xl sm:text-2xl"> / {result.total}</span></p>
              <p className="font-vt text-lg sm:text-xl text-[#4CAF50] mt-1 sm:mt-2">{t("bonusBonus")}: +{result.bonus}</p>
            </div>
            <p className="font-vt text-sm sm:text-base mb-2 sm:mb-4" style={{ color: result.saved ? "#4CAF50" : "#FFB300" }} data-testid="ocean-trophy-status">
              {result.saved ? t("trophySaved") : t("trophySignin")}
            </p>
            <BonusLeaderboard mode="ocean" levelIndex={9} levelName="Treasure Dive" character={character} score={result.collected} />
            <button data-testid="ocean-continue-btn" onClick={() => onContinue(result)} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs w-full">
              {t("continueBtn")}
            </button>
          </div>
        </div>
      )}

      {/* Knocked-out result (no tries left, didn't reach the chest) */}
      {phase === "final" && result && !result.completed && (
        <div className="absolute inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/85 p-3 overflow-y-auto scanlines">
          <div className="pixel-card bg-[#3a1320] p-4 sm:p-6 md:p-8 w-full max-w-md text-center pop-in my-auto max-h-[96vh] overflow-y-auto" data-testid="ocean-result-modal">
            <div className="text-4xl sm:text-5xl mb-1">🐟</div>
            <h2 className="font-pixel text-lg sm:text-2xl md:text-3xl pixel-stroke text-[#ff7a7a]">{t("diveKOFinal")}</h2>
            <p className="font-vt text-white/85 text-sm sm:text-base mt-1 sm:mt-2">{t("diveKOFinalBody")}</p>
            <div className="my-2 sm:my-4 py-2 sm:py-3 border-y-2 border-white/15">
              <p className="font-pixel text-[9px] sm:text-[10px] text-[#7fe9ff]">{t("cordobas")}</p>
              <p className="font-pixel text-3xl sm:text-4xl md:text-5xl text-white mt-1 sm:mt-2" data-testid="ocean-final-count">{result.collected}<span className="text-white/50 text-xl sm:text-2xl"> / {result.total}</span></p>
              <p className="font-vt text-lg sm:text-xl text-[#4CAF50] mt-1 sm:mt-2">{t("bonusBonus")}: +{result.bonus}</p>
            </div>
            <BonusLeaderboard mode="ocean" levelIndex={9} levelName="Treasure Dive" character={character} score={result.collected} />
            <button data-testid="ocean-continue-btn" onClick={() => onContinue(result)} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs w-full">
              {t("continueBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
